import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/api';
import LocationPicker from '../components/ui/LocationPicker';
import StripeConnectSetup from '../components/payment/StripeConnectSetup';
import {
  User,
  Lock,
  Bell,
  Shield,
  Camera,
  Loader2,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Phone,
  Mail,
  MapPin,
  Save,
  Twitter,
  CreditCard,
  Linkedin,
  Facebook,
  Instagram,
  Youtube,
  Globe,
  Github,
  Award,
  GraduationCap,
  FileText,
  Plus,
  Trash2,
  ExternalLink,
  Briefcase,
  Languages,
  Heart,
  MessageSquare,
  Send,
  Clock,
  ChevronRight,
} from 'lucide-react';

const UserSettingsPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, loading: authLoading, refreshUser } = useAuth();
  
  // Check for tab from URL params
  const getInitialTab = () => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['profile', 'extended', 'security', 'notifications', 'campaign-preferences', 'enquiries', 'donations', 'payouts'].includes(tabParam)) {
      return tabParam;
    }
    return 'profile';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // My Enquiries state
  const [myEnquiries, setMyEnquiries] = useState([]);
  const [enquiriesLoading, setEnquiriesLoading] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  
  // My Donations state
  const [myDonations, setMyDonations] = useState([]);
  const [donationsLoading, setDonationsLoading] = useState(false);
  
  // Profile form
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    avatar: '',
  });

  // Extended profile
  const [extendedProfile, setExtendedProfile] = useState({
    bio: '',
    headline: '',
    social_links: {
      twitter: '',
      linkedin: '',
      facebook: '',
      instagram: '',
      youtube: '',
      website: '',
      github: '',
    },
    achievements: [],
    certifications: [],
    education: [],
    skills: [],
    languages: [],
  });
  const [newSkill, setNewSkill] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  
  // Password form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  
  // Notification settings
  const [notifications, setNotifications] = useState({
    emailDonations: true,
    emailUpdates: true,
    emailNewsletter: false,
    smsDonations: false,
    smsUpdates: false,
  });

  // Campaign preferences for notifications
  const [campaignPreferences, setCampaignPreferences] = useState({
    selectedCategories: [],
    notificationsEnabled: true,
  });

  // Available campaign categories
  const campaignCategories = [
    { id: 'medical', name: 'Medical & Health', icon: '🏥' },
    { id: 'education', name: 'Education', icon: '📚' },
    { id: 'emergency', name: 'Emergency', icon: '🚨' },
    { id: 'nonprofit', name: 'Nonprofit', icon: '🤝' },
    { id: 'community', name: 'Community', icon: '🏘️' },
    { id: 'sports', name: 'Sports', icon: '⚽' },
    { id: 'research', name: 'Research', icon: '🔬' },
    { id: 'startup', name: 'Startup', icon: '🚀' },
    { id: 'creative', name: 'Creative', icon: '🎨' },
    { id: 'animals', name: 'Animals', icon: '🐾' },
    { id: 'environment', name: 'Environment', icon: '🌍' },
    { id: 'faith', name: 'Faith', icon: '🙏' },
  ];

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (user) {
      setProfile({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        location: user.location || '',
        avatar: user.avatar || '',
      });
      setExtendedProfile({
        bio: user.bio || '',
        headline: user.headline || '',
        social_links: {
          twitter: user.social_links?.twitter || '',
          linkedin: user.social_links?.linkedin || '',
          facebook: user.social_links?.facebook || '',
          instagram: user.social_links?.instagram || '',
          youtube: user.social_links?.youtube || '',
          website: user.social_links?.website || '',
          github: user.social_links?.github || '',
        },
        achievements: user.achievements || [],
        certifications: user.certifications || [],
        education: user.education || [],
        skills: user.skills || [],
        languages: user.languages || [],
      });
      // Load campaign preferences
      setCampaignPreferences({
        selectedCategories: user.campaign_preferences || [],
        notificationsEnabled: user.campaign_preference_notifications !== false,
      });
    }
  }, [user, isAuthenticated, authLoading, navigate]);

  // Fetch campaign notification preferences from API
  useEffect(() => {
    const fetchCampaignPreferences = async () => {
      if (!isAuthenticated) return;
      try {
        const response = await apiClient.get('/auth/campaign-notification-preferences');
        setCampaignPreferences({
          selectedCategories: response.data.campaign_preferences || [],
          notificationsEnabled: response.data.campaign_preference_notifications !== false,
        });
      } catch (err) {
        console.log('Failed to fetch campaign preferences:', err);
      }
    };
    fetchCampaignPreferences();
  }, [isAuthenticated]);

  // Fetch user's enquiries
  const fetchMyEnquiries = async () => {
    setEnquiriesLoading(true);
    try {
      const response = await apiClient.get('/enquiries/my-enquiries');
      setMyEnquiries(response.data.enquiries || []);
    } catch (err) {
      console.log('Failed to fetch enquiries:', err);
    } finally {
      setEnquiriesLoading(false);
    }
  };

  // Fetch user's donations
  const fetchMyDonations = async () => {
    setDonationsLoading(true);
    try {
      const response = await apiClient.get('/donations/user?limit=100');
      setMyDonations(response.data || []);
    } catch (err) {
      console.log('Failed to fetch donations:', err);
    } finally {
      setDonationsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && activeTab === 'enquiries') {
      fetchMyEnquiries();
    }
    if (isAuthenticated && activeTab === 'donations') {
      fetchMyDonations();
    }
  }, [isAuthenticated, activeTab]);

  // Handle user reply to enquiry
  const handleUserReply = async (enquiryId) => {
    if (!replyMessage.trim()) return;
    setReplyLoading(true);
    try {
      await apiClient.post(`/enquiries/${enquiryId}/user-reply`, {
        message: replyMessage
      });
      setReplyMessage('');
      // Refresh the enquiry
      const response = await apiClient.get(`/enquiries/${enquiryId}`);
      setSelectedEnquiry(response.data);
      fetchMyEnquiries();
    } catch (err) {
      showMessage('error', 'Failed to send reply');
    } finally {
      setReplyLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const toggleCampaignCategory = (categoryId) => {
    setCampaignPreferences(prev => {
      const isSelected = prev.selectedCategories.includes(categoryId);
      return {
        ...prev,
        selectedCategories: isSelected
          ? prev.selectedCategories.filter(id => id !== categoryId)
          : [...prev.selectedCategories, categoryId]
      };
    });
  };

  const handleSaveCampaignPreferences = async () => {
    setSaving(true);
    try {
      await apiClient.put('/auth/campaign-notification-preferences', {
        campaign_preferences: campaignPreferences.selectedCategories,
        campaign_preference_notifications: campaignPreferences.notificationsEnabled,
      });
      showMessage('success', 'Campaign preferences saved! You will receive notifications for new campaigns in your selected categories.');
    } catch (err) {
      showMessage('error', err.response?.data?.detail || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.put('/auth/profile', {
        name: profile.name,
        phone: profile.phone,
        location: profile.location,
      });
      if (refreshUser) await refreshUser();
      showMessage('success', 'Profile updated successfully!');
    } catch (err) {
      showMessage('error', err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword.length < 6) {
      showMessage('error', 'New password must be at least 6 characters');
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showMessage('error', 'New passwords do not match');
      return;
    }
    
    setSaving(true);
    try {
      await apiClient.post('/auth/change-password', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
      });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showMessage('success', 'Password changed successfully!');
    } catch (err) {
      showMessage('error', err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationUpdate = async () => {
    setSaving(true);
    try {
      await apiClient.put('/auth/notifications', notifications);
      showMessage('success', 'Notification preferences saved!');
    } catch (err) {
      showMessage('error', err.response?.data?.detail || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleExtendedProfileUpdate = async () => {
    setSaving(true);
    try {
      await apiClient.put('/auth/profile/extended', extendedProfile);
      if (refreshUser) await refreshUser();
      showMessage('success', 'Extended profile updated successfully!');
    } catch (err) {
      showMessage('error', err.response?.data?.detail || 'Failed to update extended profile');
    } finally {
      setSaving(false);
    }
  };

  // Achievement handlers
  const addAchievement = () => {
    setExtendedProfile({
      ...extendedProfile,
      achievements: [...extendedProfile.achievements, { title: '', description: '', date: '' }]
    });
  };
  const removeAchievement = (index) => {
    setExtendedProfile({
      ...extendedProfile,
      achievements: extendedProfile.achievements.filter((_, i) => i !== index)
    });
  };
  const updateAchievement = (index, field, value) => {
    const updated = [...extendedProfile.achievements];
    updated[index] = { ...updated[index], [field]: value };
    setExtendedProfile({ ...extendedProfile, achievements: updated });
  };

  // Certification handlers
  const addCertification = () => {
    setExtendedProfile({
      ...extendedProfile,
      certifications: [...extendedProfile.certifications, { name: '', issuer: '', date: '', credential_url: '' }]
    });
  };
  const removeCertification = (index) => {
    setExtendedProfile({
      ...extendedProfile,
      certifications: extendedProfile.certifications.filter((_, i) => i !== index)
    });
  };
  const updateCertification = (index, field, value) => {
    const updated = [...extendedProfile.certifications];
    updated[index] = { ...updated[index], [field]: value };
    setExtendedProfile({ ...extendedProfile, certifications: updated });
  };

  // Education handlers
  const addEducation = () => {
    setExtendedProfile({
      ...extendedProfile,
      education: [...extendedProfile.education, { institution: '', degree: '', field: '', start_year: '', end_year: '' }]
    });
  };
  const removeEducation = (index) => {
    setExtendedProfile({
      ...extendedProfile,
      education: extendedProfile.education.filter((_, i) => i !== index)
    });
  };
  const updateEducation = (index, field, value) => {
    const updated = [...extendedProfile.education];
    updated[index] = { ...updated[index], [field]: value };
    setExtendedProfile({ ...extendedProfile, education: updated });
  };

  // Skills & Languages handlers
  const addSkill = () => {
    if (newSkill.trim() && !extendedProfile.skills.includes(newSkill.trim())) {
      setExtendedProfile({ ...extendedProfile, skills: [...extendedProfile.skills, newSkill.trim()] });
      setNewSkill('');
    }
  };
  const removeSkill = (skill) => {
    setExtendedProfile({ ...extendedProfile, skills: extendedProfile.skills.filter(s => s !== skill) });
  };
  const addLanguage = () => {
    if (newLanguage.trim() && !extendedProfile.languages.includes(newLanguage.trim())) {
      setExtendedProfile({ ...extendedProfile, languages: [...extendedProfile.languages, newLanguage.trim()] });
      setNewLanguage('');
    }
  };
  const removeLanguage = (lang) => {
    setExtendedProfile({ ...extendedProfile, languages: extendedProfile.languages.filter(l => l !== lang) });
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      showMessage('error', 'Image must be less than 5MB');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'avatars');
    
    setSaving(true);
    try {
      const uploadRes = await apiClient.post('/r2/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (uploadRes.data.url) {
        await apiClient.put('/auth/profile', { avatar: uploadRes.data.url });
        setProfile(prev => ({ ...prev, avatar: uploadRes.data.url }));
        if (refreshUser) await refreshUser();
        showMessage('success', 'Avatar updated!');
      }
    } catch (err) {
      console.error('Avatar upload error:', err);
      const errorDetail = err.response?.data?.detail || 'Failed to upload avatar. Please try again.';
      showMessage('error', errorDetail);
    } finally {
      setSaving(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">{t('accountSettings')}</h1>
          <p className="text-muted-foreground">{t('manageAccountPreferences')}</p>
        </div>

        {/* Message Alert */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' 
              ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
              : 'bg-red-500/20 text-red-400 border border-red-500/30'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            {message.text}
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="profile" className="data-[state=active]:bg-emerald-500">
              <User className="w-4 h-4 mr-2" /> {t('profile')}
            </TabsTrigger>
            <TabsTrigger value="extended" className="data-[state=active]:bg-emerald-500">
              <Award className="w-4 h-4 mr-2" /> {t('extendedProfile')}
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-emerald-500">
              <Lock className="w-4 h-4 mr-2" /> {t('security')}
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-emerald-500">
              <Bell className="w-4 h-4 mr-2" /> {t('notifications')}
            </TabsTrigger>
            <TabsTrigger value="campaign-preferences" className="data-[state=active]:bg-emerald-500">
              <Heart className="w-4 h-4 mr-2" /> {t('campaignPreferences')}
            </TabsTrigger>
            <TabsTrigger value="payouts" className="data-[state=active]:bg-green-500">
              <CreditCard className="w-4 h-4 mr-2" /> Payout Setup
            </TabsTrigger>
            <TabsTrigger value="donations" className="data-[state=active]:bg-teal-500">
              <Heart className="w-4 h-4 mr-2" /> My Donations
            </TabsTrigger>
            <TabsTrigger value="enquiries" className="data-[state=active]:bg-amber-500">
              <MessageSquare className="w-4 h-4 mr-2" /> My Enquiries
              {myEnquiries.filter(e => e.replies?.length > 0 && !e.user_last_read).length > 0 && (
                <Badge className="ml-2 bg-amber-500 text-foreground text-xs">{myEnquiries.filter(e => e.replies?.length > 0 && !e.user_last_read).length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>{t('profileInformation')}</CardTitle>
                <CardDescription>{t('updatePersonalInfo')}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleProfileUpdate} className="space-y-6">
                  {/* Avatar */}
                  <div className="flex items-center gap-6">
                    <div className="relative">
                      <img
                        src={profile.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.name)}&background=0891b2&color=fff`}
                        alt="Avatar"
                        className="w-24 h-24 rounded-full object-cover border-4 border-emerald-500/30"
                      />
                      <label className="absolute bottom-0 right-0 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center cursor-pointer hover:bg-emerald-600 transition-colors">
                        <Camera className="w-4 h-4 text-white" />
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarChange}
                          className="hidden"
                        />
                      </label>
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground">{profile.name}</h3>
                      <p className="text-sm text-muted-foreground">{profile.email}</p>
                      {user?.member_id && (
                        <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                          <Shield className="w-4 h-4 text-emerald-500" />
                          <span className="text-sm font-mono font-semibold text-emerald-500">ID: {user.member_id}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Member ID Info Card */}
                  {user?.member_id && (
                    <div className="p-4 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center">
                          <Shield className="w-6 h-6 text-emerald-500" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Your FundTheHope Member ID</p>
                          <p className="text-2xl font-bold font-mono text-foreground">{user.member_id}</p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-3">
                        Share this ID when contacting customer support for faster assistance.
                      </p>
                    </div>
                  )}

                  {/* Name */}
                  <div>
                    <Label htmlFor="name">{t('fullName')}</Label>
                    <div className="relative mt-2">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="name"
                        value={profile.name}
                        onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                        className="pl-10 border-2 border-emerald-500/40"
                        placeholder={t('enterYourName')}
                      />
                    </div>
                  </div>

                  {/* Email (read-only) */}
                  <div>
                    <Label htmlFor="email">{t('emailAddress')}</Label>
                    <div className="relative mt-2">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="email"
                        value={profile.email}
                        disabled
                        className="pl-10 bg-secondary border-2 border-emerald-500/40"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{t('emailCannotBeChanged')}</p>
                  </div>

                  {/* Phone */}
                  <div>
                    <Label htmlFor="phone">{t('phoneNumber')}</Label>
                    <div className="relative mt-2">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="phone"
                        value={profile.phone}
                        onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                        className="pl-10 border-2 border-emerald-500/40"
                        placeholder="+1 234 567 8900"
                      />
                    </div>
                  </div>

                  {/* Location */}
                  <div>
                    <Label htmlFor="location">{t('location')}</Label>
                    <div className="mt-2">
                      <LocationPicker
                        value={profile.location}
                        onChange={(locationData) => {
                          const locationValue = locationData?.display || locationData || '';
                          setProfile({ ...profile, location: locationValue });
                        }}
                        placeholder="City, Country"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-gradient-to-r from-emerald-500 to-teal-600"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Extended Profile Tab */}
          <TabsContent value="extended">
            <div className="space-y-6">
              {/* View Public Profile Link */}
              {user?.id && (
                <div className="flex justify-end">
                  <Link to={`/profile/${user.id}`}>
                    <Button variant="outline" size="sm">
                      <ExternalLink className="w-4 h-4 mr-2" /> View Public Profile
                    </Button>
                  </Link>
                </div>
              )}

              {/* Bio & Headline */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5 text-emerald-500" /> Bio & Headline
                  </CardTitle>
                  <CardDescription>Tell donors about yourself</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Professional Headline</Label>
                    <Input
                      value={extendedProfile.headline}
                      onChange={(e) => setExtendedProfile({ ...extendedProfile, headline: e.target.value })}
                      placeholder="e.g., Social Entrepreneur | Healthcare Advocate"
                      className="mt-1 border-2 border-emerald-500/40"
                    />
                  </div>
                  <div>
                    <Label>Bio</Label>
                    <textarea
                      value={extendedProfile.bio}
                      onChange={(e) => setExtendedProfile({ ...extendedProfile, bio: e.target.value })}
                      placeholder="Tell your story... What motivates you? What causes do you care about?"
                      rows={5}
                      className="w-full mt-1 px-3 py-2 bg-background border-2 border-emerald-500/40 rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Social Media Links */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-blue-500" /> Social Media Links
                  </CardTitle>
                  <CardDescription>Connect your social profiles for better trust</CardDescription>
                </CardHeader>
                <CardContent className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2"><Twitter className="w-4 h-4 text-emerald-500" /> Twitter</Label>
                    <Input
                      value={extendedProfile.social_links.twitter}
                      onChange={(e) => setExtendedProfile({ ...extendedProfile, social_links: { ...extendedProfile.social_links, twitter: e.target.value } })}
                      placeholder="@username"
                      className="mt-1 border-2 border-emerald-500/40"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2"><Linkedin className="w-4 h-4 text-blue-600" /> LinkedIn</Label>
                    <Input
                      value={extendedProfile.social_links.linkedin}
                      onChange={(e) => setExtendedProfile({ ...extendedProfile, social_links: { ...extendedProfile.social_links, linkedin: e.target.value } })}
                      placeholder="username or full URL"
                      className="mt-1 border-2 border-emerald-500/40"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2"><Facebook className="w-4 h-4 text-blue-500" /> Facebook</Label>
                    <Input
                      value={extendedProfile.social_links.facebook}
                      onChange={(e) => setExtendedProfile({ ...extendedProfile, social_links: { ...extendedProfile.social_links, facebook: e.target.value } })}
                      placeholder="username or page"
                      className="mt-1 border-2 border-emerald-500/40"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2"><Instagram className="w-4 h-4 text-emerald-500" /> Instagram</Label>
                    <Input
                      value={extendedProfile.social_links.instagram}
                      onChange={(e) => setExtendedProfile({ ...extendedProfile, social_links: { ...extendedProfile.social_links, instagram: e.target.value } })}
                      placeholder="@username"
                      className="mt-1 border-2 border-emerald-500/40"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2"><Youtube className="w-4 h-4 text-red-500" /> YouTube</Label>
                    <Input
                      value={extendedProfile.social_links.youtube}
                      onChange={(e) => setExtendedProfile({ ...extendedProfile, social_links: { ...extendedProfile.social_links, youtube: e.target.value } })}
                      placeholder="channel name or URL"
                      className="mt-1 border-2 border-emerald-500/40"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2"><Github className="w-4 h-4" /> GitHub</Label>
                    <Input
                      value={extendedProfile.social_links.github}
                      onChange={(e) => setExtendedProfile({ ...extendedProfile, social_links: { ...extendedProfile.social_links, github: e.target.value } })}
                      placeholder="username"
                      className="mt-1 border-2 border-emerald-500/40"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="flex items-center gap-2"><Globe className="w-4 h-4 text-emerald-500" /> Website</Label>
                    <Input
                      value={extendedProfile.social_links.website}
                      onChange={(e) => setExtendedProfile({ ...extendedProfile, social_links: { ...extendedProfile.social_links, website: e.target.value } })}
                      placeholder="https://yourwebsite.com"
                      className="mt-1 border-2 border-emerald-500/40"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Education */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-blue-500" /> Education
                  </CardTitle>
                  <CardDescription>Your educational background</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {extendedProfile.education.map((edu, index) => (
                    <div key={index} className="p-4 bg-secondary/50 rounded-lg space-y-3">
                      <div className="flex justify-between items-start">
                        <span className="text-sm text-muted-foreground">Education #{index + 1}</span>
                        <Button variant="ghost" size="sm" onClick={() => removeEducation(index)} className="text-red-500 hover:text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <Input
                          placeholder="Institution"
                          value={edu.institution}
                          onChange={(e) => updateEducation(index, 'institution', e.target.value)}
                          className="border-2 border-emerald-500/40"
                        />
                        <Input
                          placeholder="Degree (e.g., Bachelor's, Master's)"
                          value={edu.degree}
                          onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                          className="border-2 border-emerald-500/40"
                        />
                        <Input
                          placeholder="Field of Study"
                          value={edu.field || ''}
                          onChange={(e) => updateEducation(index, 'field', e.target.value)}
                          className="border-2 border-emerald-500/40"
                        />
                        <div className="flex gap-2">
                          <Input
                            placeholder="Start Year"
                            value={edu.start_year || ''}
                            onChange={(e) => updateEducation(index, 'start_year', e.target.value)}
                            className="border-2 border-emerald-500/40"
                          />
                          <Input
                            placeholder="End Year"
                            value={edu.end_year || ''}
                            onChange={(e) => updateEducation(index, 'end_year', e.target.value)}
                            className="border-2 border-emerald-500/40"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <Button type="button" variant="outline" onClick={addEducation} className="w-full">
                    <Plus className="w-4 h-4 mr-2" /> Add Education
                  </Button>
                </CardContent>
              </Card>

              {/* Certifications */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-emerald-500" /> Certifications
                  </CardTitle>
                  <CardDescription>Professional certifications and credentials</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {extendedProfile.certifications.map((cert, index) => (
                    <div key={index} className="p-4 bg-secondary/50 rounded-lg space-y-3">
                      <div className="flex justify-between items-start">
                        <span className="text-sm text-muted-foreground">Certification #{index + 1}</span>
                        <Button variant="ghost" size="sm" onClick={() => removeCertification(index)} className="text-red-500 hover:text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <Input
                          placeholder="Certification Name"
                          value={cert.name}
                          onChange={(e) => updateCertification(index, 'name', e.target.value)}
                          className="border-2 border-emerald-500/40"
                        />
                        <Input
                          placeholder="Issuing Organization"
                          value={cert.issuer}
                          onChange={(e) => updateCertification(index, 'issuer', e.target.value)}
                          className="border-2 border-emerald-500/40"
                        />
                        <Input
                          placeholder="Date (e.g., Jan 2024)"
                          value={cert.date || ''}
                          onChange={(e) => updateCertification(index, 'date', e.target.value)}
                          className="border-2 border-emerald-500/40"
                        />
                        <Input
                          placeholder="Credential URL (optional)"
                          value={cert.credential_url || ''}
                          onChange={(e) => updateCertification(index, 'credential_url', e.target.value)}
                          className="border-2 border-emerald-500/40"
                        />
                      </div>
                    </div>
                  ))}
                  <Button type="button" variant="outline" onClick={addCertification} className="w-full">
                    <Plus className="w-4 h-4 mr-2" /> Add Certification
                  </Button>
                </CardContent>
              </Card>

              {/* Achievements */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-amber-500" /> Achievements & Accolades
                  </CardTitle>
                  <CardDescription>Awards, honors, and notable accomplishments</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {extendedProfile.achievements.map((ach, index) => (
                    <div key={index} className="p-4 bg-secondary/50 rounded-lg space-y-3">
                      <div className="flex justify-between items-start">
                        <span className="text-sm text-muted-foreground">Achievement #{index + 1}</span>
                        <Button variant="ghost" size="sm" onClick={() => removeAchievement(index)} className="text-red-500 hover:text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <Input
                          placeholder="Achievement Title"
                          value={ach.title}
                          onChange={(e) => updateAchievement(index, 'title', e.target.value)}
                          className="border-2 border-emerald-500/40"
                        />
                        <Input
                          placeholder="Date (e.g., 2023)"
                          value={ach.date || ''}
                          onChange={(e) => updateAchievement(index, 'date', e.target.value)}
                          className="border-2 border-emerald-500/40"
                        />
                        <div className="md:col-span-2">
                          <Input
                            placeholder="Description"
                            value={ach.description || ''}
                            onChange={(e) => updateAchievement(index, 'description', e.target.value)}
                            className="border-2 border-emerald-500/40"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Input
                            placeholder="Verification URL (optional - e.g., news article, LinkedIn post)"
                            value={ach.url || ''}
                            onChange={(e) => updateAchievement(index, 'url', e.target.value)}
                            className="border-2 border-emerald-500/40"
                          />
                          <p className="text-xs text-muted-foreground mt-1">Add a link where backers can verify this achievement</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  <Button type="button" variant="outline" onClick={addAchievement} className="w-full">
                    <Plus className="w-4 h-4 mr-2" /> Add Achievement
                  </Button>
                </CardContent>
              </Card>

              {/* Skills & Languages */}
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-teal-500" /> Skills
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add a skill"
                        value={newSkill}
                        onChange={(e) => setNewSkill(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                        className="border-2 border-emerald-500/40"
                      />
                      <Button type="button" onClick={addSkill} size="sm">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {extendedProfile.skills.map((skill, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 px-3 py-1 bg-teal-500/20 text-teal-400 rounded-full text-sm">
                          {skill}
                          <button onClick={() => removeSkill(skill)} className="hover:text-red-400">×</button>
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Languages className="w-5 h-5 text-orange-500" /> Languages
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add a language"
                        value={newLanguage}
                        onChange={(e) => setNewLanguage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLanguage())}
                        className="border-2 border-emerald-500/40"
                      />
                      <Button type="button" onClick={addLanguage} size="sm">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {extendedProfile.languages.map((lang, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 px-3 py-1 bg-orange-500/20 text-orange-400 rounded-full text-sm">
                          {lang}
                          <button onClick={() => removeLanguage(lang)} className="hover:text-red-400">×</button>
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Save Extended Profile Button */}
              <div className="flex justify-end">
                <Button onClick={handleExtendedProfileUpdate} disabled={saving} className="bg-gradient-to-r from-emerald-500 to-teal-600">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Extended Profile
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <Card>
              <CardHeader>
                <CardTitle>Change Password</CardTitle>
                <CardDescription>Update your password to keep your account secure</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordChange} className="space-y-6">
                  {/* Current Password */}
                  <div>
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <div className="relative mt-2">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="currentPassword"
                        type={showPasswords.current ? 'text' : 'password'}
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                        className="pl-10 pr-10"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* New Password */}
                  <div>
                    <Label htmlFor="newPassword">New Password</Label>
                    <div className="relative mt-2">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="newPassword"
                        type={showPasswords.new ? 'text' : 'password'}
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                        className="pl-10 pr-10"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Must be at least 6 characters</p>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <div className="relative mt-2">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="confirmPassword"
                        type={showPasswords.confirm ? 'text' : 'password'}
                        value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                        className="pl-10 pr-10"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={saving || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
                    className="bg-gradient-to-r from-emerald-500 to-teal-600"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
                    Update Password
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>Notification Preferences</CardTitle>
                <CardDescription>Choose how you want to receive notifications</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Email Notifications */}
                  <div>
                    <h4 className="font-medium text-foreground mb-4 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-emerald-400" /> Email Notifications
                    </h4>
                    <div className="space-y-3">
                      <label className="flex items-center justify-between p-3 bg-secondary rounded-lg cursor-pointer">
                        <span className="text-sm text-foreground">Donation received</span>
                        <input
                          type="checkbox"
                          checked={notifications.emailDonations}
                          onChange={(e) => setNotifications({ ...notifications, emailDonations: e.target.checked })}
                          className="w-4 h-4 accent-emerald-500"
                        />
                      </label>
                      <label className="flex items-center justify-between p-3 bg-secondary rounded-lg cursor-pointer">
                        <span className="text-sm text-foreground">Campaign updates</span>
                        <input
                          type="checkbox"
                          checked={notifications.emailUpdates}
                          onChange={(e) => setNotifications({ ...notifications, emailUpdates: e.target.checked })}
                          className="w-4 h-4 accent-emerald-500"
                        />
                      </label>
                      <label className="flex items-center justify-between p-3 bg-secondary rounded-lg cursor-pointer">
                        <span className="text-sm text-foreground">Newsletter & promotions</span>
                        <input
                          type="checkbox"
                          checked={notifications.emailNewsletter}
                          onChange={(e) => setNotifications({ ...notifications, emailNewsletter: e.target.checked })}
                          className="w-4 h-4 accent-emerald-500"
                        />
                      </label>
                    </div>
                  </div>

                  {/* SMS Notifications */}
                  <div>
                    <h4 className="font-medium text-foreground mb-4 flex items-center gap-2">
                      <Phone className="w-4 h-4 text-teal-400" /> SMS Notifications
                    </h4>
                    <div className="space-y-3">
                      <label className="flex items-center justify-between p-3 bg-secondary rounded-lg cursor-pointer">
                        <span className="text-sm text-foreground">Donation received</span>
                        <input
                          type="checkbox"
                          checked={notifications.smsDonations}
                          onChange={(e) => setNotifications({ ...notifications, smsDonations: e.target.checked })}
                          className="w-4 h-4 accent-teal-500"
                        />
                      </label>
                      <label className="flex items-center justify-between p-3 bg-secondary rounded-lg cursor-pointer">
                        <span className="text-sm text-foreground">Important updates</span>
                        <input
                          type="checkbox"
                          checked={notifications.smsUpdates}
                          onChange={(e) => setNotifications({ ...notifications, smsUpdates: e.target.checked })}
                          className="w-4 h-4 accent-teal-500"
                        />
                      </label>
                    </div>
                  </div>

                  <Button
                    onClick={handleNotificationUpdate}
                    disabled={saving}
                    className="bg-gradient-to-r from-emerald-500 to-teal-600"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Preferences
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Campaign Preferences Tab */}
          <TabsContent value="campaign-preferences">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-emerald-400" />
                  Campaign Preferences
                </CardTitle>
                <CardDescription>
                  Select campaign categories you are interested in. You will receive notifications when new campaigns matching your preferences are posted.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg border border-border">
                  <div>
                    <h4 className="font-medium text-foreground">Enable Campaign Notifications</h4>
                    <p className="text-sm text-muted-foreground">
                      Receive notifications when campaigns matching your interests are posted
                    </p>
                  </div>
                  <button
                    onClick={() => setCampaignPreferences(prev => ({
                      ...prev,
                      notificationsEnabled: !prev.notificationsEnabled
                    }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      campaignPreferences.notificationsEnabled ? 'bg-emerald-500' : 'bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        campaignPreferences.notificationsEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Category Selection */}
                <div>
                  <Label className="text-base font-medium">Select Categories</Label>
                  <p className="text-sm text-muted-foreground mb-4">
                    Choose categories you want to follow. You can select multiple categories.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {campaignCategories.map((category) => {
                      const isSelected = campaignPreferences.selectedCategories.includes(category.id);
                      return (
                        <button
                          key={category.id}
                          onClick={() => toggleCampaignCategory(category.id)}
                          disabled={!campaignPreferences.notificationsEnabled}
                          className={`p-4 rounded-xl border-2 text-left transition-all ${
                            isSelected
                              ? 'border-emerald-500 bg-emerald-500/10 text-foreground'
                              : 'border-border bg-secondary/50 text-muted-foreground hover:border-emerald-500/50'
                          } ${!campaignPreferences.notificationsEnabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <span className="text-2xl mb-2 block">{category.icon}</span>
                          <span className="font-medium text-sm">{category.name}</span>
                          {isSelected && (
                            <CheckCircle className="w-4 h-4 text-emerald-400 absolute top-2 right-2" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Selected Summary */}
                {campaignPreferences.selectedCategories.length > 0 && (
                  <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                    <h4 className="font-medium text-emerald-400 mb-2">
                      Selected Categories ({campaignPreferences.selectedCategories.length})
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {campaignPreferences.selectedCategories.map(catId => {
                        const cat = campaignCategories.find(c => c.id === catId);
                        return cat ? (
                          <span
                            key={catId}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-500/20 text-emerald-300 rounded-full text-sm"
                          >
                            {cat.icon} {cat.name}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}

                {/* Info Box */}
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <h4 className="font-medium text-blue-400 flex items-center gap-2 mb-2">
                    <Bell className="w-4 h-4" /> How it works
                  </h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• When a new campaign is posted in your selected categories, you will be notified</li>
                    <li>• Notifications appear in the bell icon at the top of the page</li>
                    <li>• You will also receive an email notification (if enabled)</li>
                    <li>• Click on a notification to view the campaign directly</li>
                  </ul>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t border-border">
                  <Button
                    onClick={handleSaveCampaignPreferences}
                    disabled={saving}
                    className="bg-gradient-to-r from-emerald-500 to-teal-600"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Campaign Preferences
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* My Enquiries Tab */}
          <TabsContent value="enquiries">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-amber-500" />
                  My Support Enquiries
                </CardTitle>
                <CardDescription>
                  View and manage your submitted enquiries and support requests. Reply to admin messages here.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {enquiriesLoading ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mx-auto mb-2" />
                    <p className="text-muted-foreground">Loading your enquiries...</p>
                  </div>
                ) : myEnquiries.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-foreground font-medium mb-2">No enquiries yet</p>
                    <p className="text-muted-foreground text-sm mb-4">
                      You haven't submitted any support requests or enquiries.
                    </p>
                    <Button variant="outline" onClick={() => navigate('/contact')}>
                      Contact Support
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Enquiry List */}
                    {!selectedEnquiry ? (
                      myEnquiries.map((enquiry) => (
                        <div
                          key={enquiry.id}
                          onClick={async () => {
                            setSelectedEnquiry(enquiry);
                            // Mark as read
                            try {
                              await apiClient.put(`/enquiries/${enquiry.id}/mark-read`);
                            } catch (err) {
                              console.log('Failed to mark as read:', err);
                            }
                          }}
                          className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                            enquiry.replies?.length > 0 && !enquiry.user_last_read
                              ? 'bg-amber-500/10 border-amber-500/30'
                              : 'bg-card border-border'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge className={
                                enquiry.type === 'contact' ? 'bg-blue-500/20 text-blue-400' :
                                enquiry.type === 'support' ? 'bg-purple-500/20 text-purple-400' :
                                enquiry.type === 'report' ? 'bg-red-500/20 text-red-400' :
                                enquiry.type === 'partnership' ? 'bg-green-500/20 text-green-400' :
                                'bg-amber-500/20 text-amber-400'
                              }>
                                {enquiry.type?.charAt(0).toUpperCase() + enquiry.type?.slice(1)}
                              </Badge>
                              <Badge className={
                                enquiry.status === 'new' ? 'bg-yellow-500/20 text-yellow-400' :
                                enquiry.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                                enquiry.status === 'resolved' ? 'bg-green-500/20 text-green-400' :
                                'bg-slate-500/20 text-muted-foreground'
                              }>
                                {enquiry.status?.replace('_', ' ')}
                              </Badge>
                              {enquiry.replies?.length > 0 && !enquiry.user_last_read && (
                                <Badge className="bg-amber-500 text-white animate-pulse">New Reply!</Badge>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(enquiry.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <h4 className="font-semibold text-foreground mb-1">{enquiry.subject}</h4>
                          <p className="text-sm text-muted-foreground line-clamp-2">{enquiry.message}</p>
                          {enquiry.replies?.length > 0 && (
                            <p className="text-xs text-emerald-400 mt-2">
                              💬 {enquiry.replies.length} replies in conversation
                            </p>
                          )}
                        </div>
                      ))
                    ) : (
                      // Selected Enquiry Detail View
                      <div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="mb-4 text-muted-foreground hover:text-foreground"
                          onClick={() => {
                            setSelectedEnquiry(null);
                            setReplyMessage('');
                          }}
                        >
                          ← Back to all enquiries
                        </Button>
                        
                        {/* Enquiry Header */}
                        <div className="bg-card border border-border rounded-lg p-4 mb-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Badge className={
                              selectedEnquiry.type === 'contact' ? 'bg-blue-500/20 text-blue-400' :
                              selectedEnquiry.type === 'support' ? 'bg-purple-500/20 text-purple-400' :
                              selectedEnquiry.type === 'report' ? 'bg-red-500/20 text-red-400' :
                              selectedEnquiry.type === 'partnership' ? 'bg-green-500/20 text-green-400' :
                              'bg-amber-500/20 text-amber-400'
                            }>
                              {selectedEnquiry.type?.charAt(0).toUpperCase() + selectedEnquiry.type?.slice(1)}
                            </Badge>
                            <Badge className={
                              selectedEnquiry.status === 'new' ? 'bg-yellow-500/20 text-yellow-400' :
                              selectedEnquiry.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                              selectedEnquiry.status === 'resolved' ? 'bg-green-500/20 text-green-400' :
                              'bg-slate-500/20 text-muted-foreground'
                            }>
                              {selectedEnquiry.status?.replace('_', ' ')}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {new Date(selectedEnquiry.created_at).toLocaleString()}
                            </span>
                          </div>
                          <h3 className="text-lg font-semibold text-foreground mb-2">{selectedEnquiry.subject}</h3>
                          <p className="text-muted-foreground text-sm whitespace-pre-wrap">{selectedEnquiry.message}</p>
                        </div>
                        
                        {/* Conversation Thread */}
                        {selectedEnquiry.replies && selectedEnquiry.replies.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-medium text-muted-foreground mb-3">
                              Conversation ({selectedEnquiry.replies.length} replies)
                            </h4>
                            <div className="space-y-3 max-h-[300px] overflow-y-auto">
                              {selectedEnquiry.replies.map((reply, index) => (
                                <div
                                  key={reply.id || index}
                                  className={`p-3 rounded-lg ${
                                    reply.sender_role === 'user'
                                      ? 'bg-blue-500/10 border-l-2 border-blue-500 ml-4'
                                      : 'bg-emerald-500/10 border-l-2 border-emerald-500 mr-4'
                                  }`}
                                >
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={`text-xs font-medium ${
                                      reply.sender_role === 'user' ? 'text-blue-400' : 'text-emerald-400'
                                    }`}>
                                      {reply.sender_role === 'user' ? '👤 You' : '🛡️ Support Team'}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      {new Date(reply.created_at).toLocaleString()}
                                    </span>
                                  </div>
                                  <p className="text-foreground text-sm whitespace-pre-wrap">{reply.message}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Reply Form */}
                        {selectedEnquiry.status !== 'resolved' && (
                          <div className="border-t border-border pt-4">
                            <h4 className="text-sm font-medium text-foreground mb-2">
                              Send a Reply
                            </h4>
                            <Textarea
                              value={replyMessage}
                              onChange={(e) => setReplyMessage(e.target.value)}
                              placeholder="Type your message here..."
                              className="mb-3"
                            />
                            <Button
                              onClick={() => handleUserReply(selectedEnquiry.id)}
                              disabled={!replyMessage.trim() || replyLoading}
                              className="bg-emerald-500 hover:bg-emerald-600"
                            >
                              {replyLoading ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                              ) : (
                                <><Send className="w-4 h-4 mr-2" /> Send Reply</>
                              )}
                            </Button>
                          </div>
                        )}
                        
                        {selectedEnquiry.status === 'resolved' && (
                          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                            <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                            <p className="text-green-400 font-medium">This enquiry has been resolved</p>
                            <p className="text-muted-foreground text-sm">
                              Need more help? <button onClick={() => navigate('/contact')} className="text-emerald-400 hover:underline">Submit a new enquiry</button>
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payout Setup Tab */}
          <TabsContent value="payouts">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-green-500" />
                  Payout Setup
                </CardTitle>
                <CardDescription>
                  Connect your Stripe account to receive withdrawals from your campaigns.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {user?.kyc_status === 'approved' ? (
                  <StripeConnectSetup />
                ) : (
                  <div className="text-center py-8">
                    <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                    <p className="text-foreground font-medium mb-2">KYC Verification Required</p>
                    <p className="text-muted-foreground text-sm mb-4">
                      You need to complete KYC verification before setting up payouts.
                    </p>
                    <Button onClick={() => navigate('/verification')} className="bg-emerald-500 hover:bg-emerald-600">
                      Complete Verification
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* My Donations Tab */}
          <TabsContent value="donations">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-teal-500" />
                  My Donation History
                </CardTitle>
                <CardDescription>
                  View all your past donations and track your contributions to campaigns.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {donationsLoading ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-teal-500 mx-auto mb-2" />
                    <p className="text-muted-foreground">Loading your donation history...</p>
                  </div>
                ) : myDonations.length === 0 ? (
                  <div className="text-center py-12">
                    <Heart className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                    <p className="text-foreground font-medium mb-2">No donations yet</p>
                    <p className="text-muted-foreground text-sm mb-4">
                      You have not made any donations yet. Start supporting causes you care about!
                    </p>
                    <Button onClick={() => navigate('/discover')} className="bg-teal-500 hover:bg-teal-600">
                      Explore Campaigns
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Summary Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-4 text-center">
                        <p className="text-teal-400 text-sm font-medium">Total Donated</p>
                        <p className="text-2xl font-bold text-foreground">
                          ${myDonations.reduce((sum, d) => sum + (d.amount || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 text-center">
                        <p className="text-emerald-400 text-sm font-medium">Total Donations</p>
                        <p className="text-2xl font-bold text-foreground">{myDonations.length}</p>
                      </div>
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-center">
                        <p className="text-blue-400 text-sm font-medium">Campaigns Supported</p>
                        <p className="text-2xl font-bold text-foreground">
                          {new Set(myDonations.map(d => d.campaign_id)).size}
                        </p>
                      </div>
                    </div>

                    {/* Donations List */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-3 px-4 text-muted-foreground font-medium text-sm">Date</th>
                            <th className="text-left py-3 px-4 text-muted-foreground font-medium text-sm">Campaign</th>
                            <th className="text-right py-3 px-4 text-muted-foreground font-medium text-sm">Amount</th>
                            <th className="text-center py-3 px-4 text-muted-foreground font-medium text-sm">Status</th>
                            <th className="text-center py-3 px-4 text-muted-foreground font-medium text-sm">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {myDonations.map((donation) => (
                            <tr key={donation.id} className="border-b border-border/50 hover:bg-secondary/30">
                              <td className="py-3 px-4 text-foreground text-sm">
                                {new Date(donation.created_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  <div>
                                    <p className="text-foreground font-medium text-sm truncate max-w-[200px]">
                                      {donation.campaign_title || 'Unknown Campaign'}
                                    </p>
                                    {donation.message && (
                                      <p className="text-muted-foreground text-xs truncate max-w-[200px]">
                                        &quot;{donation.message}&quot;
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-right">
                                <span className="text-teal-400 font-semibold">
                                  ${donation.amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <Badge className={
                                  donation.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                  donation.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                  donation.status === 'refunded' ? 'bg-red-500/20 text-red-400' :
                                  'bg-emerald-500/20 text-emerald-400'
                                }>
                                  {donation.status || 'completed'}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/campaign/${donation.campaign_id}`)}
                                  className="text-muted-foreground hover:text-foreground"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default UserSettingsPage;
